<template>
  <div class="fc-home">
    <!-- ───────── HERO / WELCOME ───────── -->
    <header class="fc-hero">
      <div class="fc-hero__left">
        <span class="fc-hero__eyebrow">
          <v-icon size="16" color="primary">mdi-view-dashboard-outline</v-icon>
          Panel operativo
        </span>
        <h1 class="fc-hero__title">{{ greeting }} 👋</h1>
        <p v-if="hasDashboardAccess" class="fc-hero__lede">
          Aquí está el estado de tus operaciones, consolidado para
          <strong>{{ businessLabel }}</strong> · {{ locationLabel }}.
        </p>
        <p v-else class="fc-hero__lede">
          Que tengas un excelente día.
        </p>
      </div>

      <div class="fc-hero__right">
        <div class="fc-hero__date">
          <span class="fc-hero__date-day">{{ today.day }}</span>
          <span class="fc-hero__date-rest">
            <span class="fc-hero__date-month">{{ today.month }}</span>
            <span class="fc-hero__date-year">{{ today.year }}</span>
          </span>
        </div>
        <span class="fc-chip-live">
          <span class="fc-chip-live__dot"></span>
          Datos en vivo
        </span>
      </div>
    </header>

    <!-- ───────── FILTERS ───────── -->
    <section v-if="hasDashboardAccess" class="fc-section">
      <div class="fc-section__head">
        <v-icon size="20" color="primary">mdi-filter-variant</v-icon>
        <h2 class="fc-section__title">Selección</h2>
      </div>
      <div class="fc-card fc-card--filters">
        <FilterBussinesLocations
          @filter_loaded="update_data"
          :type="false"
          :data="false"
          :showRefresh="false"
          :viewPeriods="false"
        />
      </div>
    </section>

    <!-- ───────── KPI STRIP ───────── -->
    <section v-if="filters.business && canSeeKpis" class="fc-section">
      <div class="fc-section__head">
        <v-icon size="20" color="primary">mdi-chart-box-outline</v-icon>
        <h2 class="fc-section__title">Resumen ejecutivo</h2>
        <v-progress-circular
          v-if="kpiLoading"
          indeterminate
          size="16"
          width="2"
          color="primary"
          class="ml-2"
        />
        <span v-if="kpiLoading" class="fc-section__status">Actualizando…</span>
      </div>

      <div class="fc-kpis">
        <article
          v-for="kpi in kpis"
          :key="kpi.key"
          class="fc-kpi"
          :class="`fc-kpi--${kpi.tone}`"
        >
          <div class="fc-kpi__top">
            <span class="fc-kpi__icon">
              <v-icon size="22" :color="kpi.color">{{ kpi.icon }}</v-icon>
            </span>
            <span
              v-if="kpi.delta !== null"
              class="fc-kpi__delta"
              :class="kpi.delta >= 0 ? 'is-up' : 'is-down'"
            >
              <v-icon size="14" :color="kpi.delta >= 0 ? 'success' : 'error'">
                {{ kpi.delta >= 0 ? 'mdi-arrow-up-thin' : 'mdi-arrow-down-thin' }}
              </v-icon>
              {{ Math.abs(kpi.delta).toFixed(1) }}%
            </span>
          </div>

          <span class="fc-kpi__label">{{ kpi.label }}</span>
          <div class="fc-kpi__value">
            <span class="fc-kpi__number">{{ kpi.value }}</span>
            <span class="fc-kpi__currency">{{ kpi.currency }}</span>
          </div>
          <span class="fc-kpi__sub">{{ kpi.sub }}</span>

          <div class="fc-kpi__spark">
            <svg :viewBox="`0 0 ${Math.max(kpi.spark.length - 1, 1) * 10} 30`" preserveAspectRatio="none">
              <polyline
                :points="sparkPoints(kpi.spark)"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
                stroke-linecap="round"
              />
            </svg>
          </div>
        </article>
      </div>
    </section>

    <!-- ───────── TABS / SECTIONS ───────── -->
    <section class="fc-section" v-if="!loading && dashTypes.types.length > 0">
      <div class="fc-section__head">
        <v-icon size="20" color="primary">mdi-folder-multiple-outline</v-icon>
        <h2 class="fc-section__title">Secciones</h2>
      </div>

      <div class="fc-card fc-card--board">
        <v-tabs
          v-model="tab"
          color="primary"
          slider-color="primary"
          slider-size="3"
          show-arrows
          class="fc-tabs"
        >
          <v-tab
            v-for="item of dashTypes.types"
            :key="'tab-' + item.id"
            class="fc-tab"
          >
            {{ item.name }}
          </v-tab>
        </v-tabs>

        <v-divider />

        <div class="fc-board__pane">
          <v-tabs-items v-model="tab" style="background:transparent;">
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-021')">
              <DashboardAdmin :filters="filters" />
            </v-tab-item>
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-022')">
              <DashboardSales :filters="filters" />
            </v-tab-item>
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-023')">
              <DashboardCompra :filters="filters" />
            </v-tab-item>
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-024')">
              <!-- <DashboardAccounting :filter="filters" /> -->
            </v-tab-item>
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-025')">
              <!-- <DashboardInventory :filter="filters" /> -->
            </v-tab-item>
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-026')">
              <DashboardCxC :filter="filters" />
            </v-tab-item>
            <v-tab-item v-if="$store.state.auth.globalFeatures.includes('SYSFC-027')">
              <DashboardCxP :filter="filters" />
            </v-tab-item>
          </v-tabs-items>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from "moment";
moment.locale("es");
import DashboardAdmin from "@/components/Dashboard.vue";
import DashboardCompra from "@/components/Shop/Dashboard.vue";
import DashboardCxC from "@/components/Transactions/DashboardCxC.vue";
import DashboardCxP from "@/components/Transactions/DashboardCxP.vue";
import DashboardInventory from "@/components/Inventory/Dashboard.vue";
import DashboardAccounting from "@/components/Accounting/Dashboards/Gastos.vue";
import FilterBussinesLocations from "@/components/FilterBussinesLocations.vue";
import DashboardSales from "@/components/Income/Dashboard.vue";

export default {
  name: "Home",
  components: {
    DashboardAdmin, DashboardSales, DashboardCompra, FilterBussinesLocations,
    DashboardAccounting, DashboardCxC, DashboardCxP, DashboardInventory,
  },

  data: () => ({
    loading: false,
    kpiLoading: false,
    filters: { business: null, location: null, department_id: null },
    tab: null,
    rawKpis: null,
  }),

  computed: {
    today() {
      const m = moment();
      return {
        day: m.format("DD"),
        month: m.format("MMMM").toUpperCase(),
        year: m.format("YYYY"),
      };
    },
    greeting() {
      const h = moment().hour();
      if (h < 12) return "Buenos días";
      if (h < 19) return "Buenas tardes";
      return "Buenas noches";
    },
    businessLabel() {
      if (!this.filters.business) return "todas las unidades";
      const bu = this.$store.state.auth.bussines_units?.find(
        (b) => b.value === this.filters.business
      );
      return bu ? (bu.text || bu.description_short || bu.description_long) : "unidad seleccionada";
    },
    locationLabel() {
      if (!this.filters.location || this.filters.location === 0) return "Todas las ubicaciones";
      const loc = this.$store.state.auth.locations?.find(
        (l) => l.id === this.filters.location || l.value === this.filters.location
      );
      return loc ? (loc.text || loc.desc_loc_long || loc.desc_loc_short) : "ubicación seleccionada";
    },
    currencyCode() {
      const bu = this.$store.state.auth.bussines_units?.find((b) => b.value === this.filters.business);
      return bu?.currency_code || "";
    },
    kpis() {
      const empty = { value: "—", delta: null, spark: [4,4,4,4,4,4,4,4] };
      const data = this.rawKpis || {};
      return [
        {
          key: "ingresos", label: "Ingresos", tone: "primary", color: "primary",
          icon: "mdi-trending-up",
          value: data.ingresos?.value ?? empty.value,
          currency: this.currencyCode,
          delta: data.ingresos?.delta ?? null,
          sub: "vs. mes anterior",
          spark: data.ingresos?.spark ?? empty.spark,
        },
        {
          key: "compras", label: "Compras", tone: "info", color: "info",
          icon: "mdi-cart-outline",
          value: data.compras?.value ?? empty.value,
          currency: this.currencyCode,
          delta: data.compras?.delta ?? null,
          sub: "vs. mes anterior",
          spark: data.compras?.spark ?? empty.spark,
        },
        {
          key: "cxc", label: "Por cobrar", tone: "accent", color: "accent",
          icon: "mdi-wallet-outline",
          value: data.cxc?.value ?? empty.value,
          currency: this.currencyCode,
          delta: null,
          sub: "Saldo pendiente",
          spark: data.cxc?.spark ?? empty.spark,
        },
        {
          key: "cxp", label: "Por pagar", tone: "warning", color: "warning",
          icon: "mdi-cash-multiple",
          value: data.cxp?.value ?? empty.value,
          currency: this.currencyCode,
          delta: null,
          sub: "Saldo pendiente",
          spark: data.cxp?.spark ?? empty.spark,
        },
      ];
    },
    dashTypes() {
      const data = [];
      const f = this.$store.state.auth.globalFeatures || [];
      if (f.includes("SYSFC-021")) data.push({ id: data.length, name: "Administrativo" });
      if (f.includes("SYSFC-022")) data.push({ id: data.length, name: "Ventas" });
      if (f.includes("SYSFC-023")) data.push({ id: data.length, name: "Compras" });
      if (f.includes("SYSFC-024")) data.push({ id: data.length, name: "Contabilidad" });
      if (f.includes("SYSFC-025")) data.push({ id: data.length, name: "Inventario" });
      if (f.includes("SYSFC-026")) data.push({ id: data.length, name: "CXC" });
      if (f.includes("SYSFC-027")) data.push({ id: data.length, name: "CXP" });
      return { types: data };
    },
    canSeeKpis() {
      const f = this.$store.state.auth.globalFeatures || [];
      return f.includes("SYSFC-020");
    },
    hasDashboardAccess() {
      return this.canSeeKpis || this.dashTypes.types.length > 0;
    },
  },

  methods: {
    sparkPoints(arr) {
      if (!arr || !arr.length) return "";
      const max = Math.max(...arr, 1);
      const min = Math.min(...arr, 0);
      const range = (max - min) || 1;
      return arr.map((v, i) => {
        const x = i * 10;
        const y = 28 - ((v - min) / range) * 24;
        return `${x},${y.toFixed(1)}`;
      }).join(" ");
    },

    computeDelta(arr) {
      if (!arr || arr.length < 2) return null;
      const last = arr[arr.length - 1];
      const prev = arr[arr.length - 2];
      if (!prev) return null;
      return ((last - prev) / prev) * 100;
    },

    compact(n) {
      if (n == null || isNaN(n)) return "—";
      const abs = Math.abs(n);
      if (abs >= 1e9) return (n / 1e9).toFixed(2) + "B";
      if (abs >= 1e6) return (n / 1e6).toFixed(2) + "M";
      if (abs >= 1e3) return (n / 1e3).toFixed(1) + "k";
      return n.toFixed(0);
    },

    async loadKpis(filter) {
      if (!filter.business || !this.canSeeKpis) return;
      this.kpiLoading = true;
      try {
        const fetchCached = (endpoint, payload) =>
          this.$store.dispatch("dashboard/fetchCached", {
            endpoint,
            payload,
            fetcher: async () => {
              const r = await this.api_Post(endpoint, payload);
              return await r.json();
            },
          });

        const [sale, shop, cxc, cxp, monthVs] = await Promise.all([
          fetchCached("income/sales_and_shop", { business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date, type: 2 }),
          fetchCached("income/sales_and_shop", { business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date, type: 1 }),
          fetchCached("income/pending_balance_amount", { type: 2, business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date }),
          fetchCached("income/pending_balance_amount", { type: 1, business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date }),
          fetchCached("income/dashboard_months_vs_last_moth", { business: filter.business, location: filter.location, from_date: filter.from_date, to_date: filter.to_date }),
        ]);

        const ingresosVal = Number(sale?.[0]?.total ?? 0);
        const comprasVal  = Number(shop?.[0]?.total ?? 0);
        const cxcVal      = cxc?.length ? Number(cxc[0].total) : 0;
        const cxpVal      = cxp?.length ? Number(cxp[0].total) : 0;

        const ingresosSpark = [], comprasSpark = [];
        if (Array.isArray(monthVs)) {
          for (const m of monthVs) {
            ingresosSpark.push(Number(m.tipos?.ingresos ?? 0));
            comprasSpark.push(Number(m.tipos?.compras ?? 0));
          }
        }
        const ingresosDelta = this.computeDelta(ingresosSpark);
        const comprasDelta  = this.computeDelta(comprasSpark);

        this.rawKpis = {
          ingresos: { value: this.compact(ingresosVal), delta: ingresosDelta, spark: ingresosSpark.length ? ingresosSpark : [3,4,3,5,6,5,7,8] },
          compras:  { value: this.compact(comprasVal),  delta: comprasDelta,  spark: comprasSpark.length ? comprasSpark : [5,4,6,5,4,6,5,6] },
          cxc:      { value: this.compact(cxcVal),      delta: null,          spark: [4,5,6,7,6,7,8,7] },
          cxp:      { value: this.compact(cxpVal),      delta: null,          spark: [6,5,6,7,6,5,4,5] },
        };
      } catch (e) {
        console.error("KPI load failed", e);
      } finally {
        this.kpiLoading = false;
      }
    },

    async update_data(filters) {
      this.filters = filters;
      this.loading = false;
      this.loadKpis(filters);
    },
  },
};
</script>

<style lang="scss" scoped>
// ── Palette aligned with the Vuetify theme (teal primary, MD tokens) ──
$ink:        #2A3547;
$ink-soft:   #46577a;
$muted:      #5A6A85;
$muted-soft: #8A98AB;
$rule:       #E5EAF2;
$bg:         #F7F9FC;
$card:       #FFFFFF;

$primary:      #00897B;
$primary-soft: #E0F2F1;
$info:         #2196F3;
$info-soft:    #E3F2FD;
$accent:       #5C6BC0;
$accent-soft:  #E8EAF6;
$warning:      #F57F17;
$warning-soft: #FFF3E0;
$success:      #2E7D32;
$error:        #E53935;

$shadow:    rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 8px 18px -6px;
$shadow-lg: rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 16%) 0px 14px 28px -6px;
$font: 'Plus Jakarta Sans', sans-serif;

.fc-home {
  font-family: $font;
  color: $ink;
  background: $bg;
  min-height: 100vh;
  padding: clamp(18px, 2.4vw, 32px) clamp(16px, 3vw, 40px) 56px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

// ── HERO ─────────────────────────────────────────────────────
.fc-hero {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  padding: clamp(22px, 2.6vw, 34px);
  border-radius: 16px;
  background: linear-gradient(120deg, #ffffff 0%, #f3faf8 100%);
  border: 1px solid $rule;
  box-shadow: $shadow;
  overflow: hidden;
}
.fc-hero::before {
  content: "";
  position: absolute;
  inset: 0 0 0 auto;
  width: 5px;
  background: linear-gradient(180deg, $primary, lighten($primary, 18%));
}
.fc-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 10px;
}
.fc-hero__title {
  font-family: $font;
  font-size: clamp(26px, 3.2vw, 36px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -.01em;
  color: $ink;
  margin: 0 0 10px;
}
.fc-hero__lede {
  font-size: 14px;
  line-height: 1.55;
  color: $muted;
  max-width: 60ch;
  margin: 0;
}
.fc-hero__lede strong { color: $ink; font-weight: 700; }

.fc-hero__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.fc-hero__date {
  display: flex;
  align-items: center;
  gap: 12px;
}
.fc-hero__date-day {
  font-size: 52px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -.03em;
  color: $primary;
}
.fc-hero__date-rest {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}
.fc-hero__date-month {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .14em;
  color: $ink;
}
.fc-hero__date-year {
  font-size: 13px;
  font-weight: 600;
  color: $muted;
}
.fc-chip-live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: $primary;
  padding: 6px 12px;
  border-radius: 999px;
  background: $primary-soft;
}
.fc-chip-live__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $primary;
  box-shadow: 0 0 0 3px rgba(0, 137, 123, .18);
  animation: fc-pulse 1.8s ease-in-out infinite;
}
@keyframes fc-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(0, 137, 123, .18); }
  50%      { box-shadow: 0 0 0 8px rgba(0, 137, 123, 0); }
}

// ── SECTION ──────────────────────────────────────────────────
.fc-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.fc-section__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -.01em;
  color: $ink;
  margin: 0;
}
.fc-section__status {
  font-size: 12px;
  font-weight: 600;
  color: $muted;
  margin-left: 4px;
}

// ── CARD ─────────────────────────────────────────────────────
.fc-card {
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
}
.fc-card--filters {
  padding: 16px clamp(14px, 1.6vw, 22px) 4px;
}
.fc-card--board {
  overflow: hidden;
}

// ── KPI ──────────────────────────────────────────────────────
.fc-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 18px;
}
.fc-kpi {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 20px 16px;
  background: $card;
  border: 1px solid $rule;
  border-radius: 14px;
  box-shadow: $shadow;
  overflow: hidden;
  transition: transform .2s ease, box-shadow .2s ease;
}
.fc-kpi:hover {
  transform: translateY(-3px);
  box-shadow: $shadow-lg;
}
.fc-kpi__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.fc-kpi__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
}
.fc-kpi--primary .fc-kpi__icon { background: $primary-soft; }
.fc-kpi--info    .fc-kpi__icon { background: $info-soft; }
.fc-kpi--accent  .fc-kpi__icon { background: $accent-soft; }
.fc-kpi--warning .fc-kpi__icon { background: $warning-soft; }
.fc-kpi__delta {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 9px 3px 6px;
  border-radius: 999px;
}
.fc-kpi__delta.is-up   { color: $success; background: rgba(46, 125, 50, .1); }
.fc-kpi__delta.is-down { color: $error;   background: rgba(229, 57, 53, .1); }
.fc-kpi__label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: $muted;
}
.fc-kpi__value {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.fc-kpi__number {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -.02em;
  color: $ink;
}
.fc-kpi__currency {
  font-size: 13px;
  font-weight: 600;
  color: $muted-soft;
  text-transform: uppercase;
}
.fc-kpi__sub {
  font-size: 12px;
  color: $muted-soft;
}
.fc-kpi__spark {
  height: 30px;
  margin-top: 6px;
  opacity: .85;
}
.fc-kpi--primary .fc-kpi__spark { color: $primary; }
.fc-kpi--info    .fc-kpi__spark { color: $info; }
.fc-kpi--accent  .fc-kpi__spark { color: $accent; }
.fc-kpi--warning .fc-kpi__spark { color: $warning; }
.fc-kpi__spark svg { width: 100%; height: 100%; display: block; }

// ── BOARD / TABS ─────────────────────────────────────────────
.fc-tabs {
  padding: 0 8px;
}
.fc-tab {
  font-family: $font;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}
.fc-board__pane {
  padding: clamp(14px, 1.8vw, 24px);
}

@media (max-width: 720px) {
  .fc-hero { grid-template-columns: 1fr; }
  .fc-hero__right { flex-direction: row; align-items: center; justify-content: space-between; }
  .fc-kpis { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 460px) {
  .fc-kpis { grid-template-columns: 1fr; }
}

// ── Match Vuetify inputs to the card aesthetic ───────────────
::v-deep .fc-card--filters {
  .v-input__slot { background: #F7F9FC !important; min-height: 44px !important; }
  .v-input fieldset { border-color: $rule !important; border-radius: 8px !important; }
  .v-input--is-focused fieldset { border-color: $primary !important; }
  .v-label { font-family: $font !important; color: $muted !important; }
  .v-select__selection { font-family: $font !important; font-size: 14px !important; color: $ink !important; }
}
::v-deep .v-tabs-items { overflow: visible !important; }
::v-deep .v-window__container { overflow: visible !important; }
</style>
